import { Card, Typography } from '../ui';
import { needsYouPrompt, stageCard, threadStage, type StageThread } from '../../../lib/outreach-stage';
import { refreshOutreachStages } from '../../../lib/contact-book';
import { isSendHoldGuid } from '../../../lib/send-hold';
import { HeldSendRow } from '../send-hold/HeldSendRow';
import { renderStageParam } from '../../../lib/outreach-stage-render';
import { useAgentName } from '../../../lib/use-agent-name';
import { useTranslation } from '../../../lib/i18n';
import { TaskDecisionPrompts } from '../tasks/TaskDecisionPrompts';
import { StageLine } from './StageLine';
import styles from './contacts.module.css';

/**
 * A thread's open question, answered in place: the SHARED prompt block, which prints the question and its
 * chips (an ANSWER kind gets its inline control), under a header that depends on the screen (rule 2):
 * - `channel` — the Inbox: "WhatsApp · {agent} is asking you";
 * - `agent` — a thread's stage card (viewer / thread page / task modal): "{agent} is asking you";
 * - `none` — the contact screen, which heads its question cards once with "Needs you".
 * A question with no text reads `outreach.line.needsYouFallback` rather than the ledger's generic title.
 *
 * The answer path is the existing VERDICT path (`useDecisionAnswer` → `/api/decision-verdict`), and there
 * is no optimistic removal: the block shows its settled sentence until the contact-book re-read
 * (`refreshOutreachStages`) moves the thread out of Needs you.
 *
 * ⚠️ Render it as a SIBLING of a row Card, never inside one (Q3): the row card is a button, and the
 * block's chips are buttons. DOM ids key off the decision (`rowKey`), since an outreach question often has
 * no task to key them on.
 */
export function StageQuestion({
  thread,
  page,
  header = 'agent',
}: {
  thread: StageThread;
  /** The BI page the block's `answer_decision_*` events report: `contact_thread`, `contacts_page`, `contact_detail`. */
  page: string;
  header?: 'channel' | 'agent' | 'none';
}) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const card = stageCard(thread);
  const prompt = needsYouPrompt(thread);
  if (card?.kind !== 'question' || !prompt) return null;
  const tr = t as (key: string, params?: Record<string, unknown>) => string;
  const asking = t(card.header.key, { maestroAgentName: agent.name });
  const channel = header === 'channel' ? renderStageParam(card.headerChannel, { t: tr, maestroAgentName: agent.name }) : '';
  // The block reads `outreachId` to refresh the contact book after an answer (rule 12), and prints the
  // fallback sentence when the Decision carries no question (rule 2).
  const blockPrompt = {
    ...prompt,
    question: prompt.question ?? t('outreach.line.needsYouFallback', { maestroAgentName: agent.name }),
    ...(thread.outreachId ? { outreachId: thread.outreachId } : {}),
  };
  return (
    <div className={styles.stageQuestion}>
      {header !== 'none' ? (
        <Typography variant="tiny-semi-bold" color="secondary">
          {channel ? `${channel} · ${asking}` : asking}
        </Typography>
      ) : null}
      <TaskDecisionPrompts
        task={{ id: thread.needsYou?.taskId ?? '' }}
        prompts={[blockPrompt]}
        origin={null}
        page={{ page_name: page }}
        rowKey={prompt.decisionId}
      />
    </div>
  );
}

/**
 * The card pinned above a thread's messages (the viewer and the thread page): the question block, the
 * scheduled/stuck line, the outcome, or "Closed (by you)". A handling thread pins nothing.
 */
export function StageCard({ thread, contactName, page }: { thread: StageThread; contactName: string; page: string }) {
  const { t } = useTranslation();
  const card = stageCard(thread);
  if (!card) return null;
  if (card.kind === 'question') return <StageQuestion thread={thread} page={page} />;
  return (
    <Card variant="stone" size="sm">
      {card.kind === 'line' ? (
        <>
          <StageLine thread={thread} contactName={contactName} variant="p2" color="primary" />
          <ScheduledCancel thread={thread} />
        </>
      ) : (
        <Typography variant="p2" color="primary" dir="auto">
          {card.kind === 'outcome' ? card.text : t(card.key)}
        </Typography>
      )}
    </Card>
  );
}

/**
 * The send rule's Cancel on a SCHEDULED thread: the waiting follow-up (`heldSend`) can be taken back from here, with
 * the same row, outcomes and one-retry Cancel as the chat chip and the task screen (surface `thread`). Only a push
 * FOLLOW-UP ever reaches a thread — a held FIRST message has no thread yet (it is keyed by the outreach it creates),
 * so its Cancel lives on the chat chip and the task screen. The stage line above already reads the send time, so this
 * row draws only the button and its outcome. After an answer the stages are re-read so the thread leaves Scheduled.
 */
function ScheduledCancel({ thread }: { thread: StageThread }) {
  const held = thread.heldSend;
  if (threadStage(thread) !== 'scheduled' || !held || !isSendHoldGuid(held.heldSendId) || !held.sendAt || Number.isNaN(Date.parse(held.sendAt))) {
    return null;
  }
  return (
    <HeldSendRow
      holdId={held.heldSendId}
      fireAt={held.sendAt}
      surface="thread"
      live="unknown"
      bare
      hideTime
      onSettled={(phase) => {
        if (phase === 'canceled' || phase === 'tooLate') void refreshOutreachStages();
      }}
    />
  );
}
